import { Body, Icon, IconType, TextLink } from '@assembly-js/design-system';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { useResizeObserver } from 'src/hooks/useResizeObserver';
import PhoneNumberInput from 'src/legacy/components/Inputs/PhoneNumberInput';
import { CustomFieldType } from 'src/store/clients/types';
import { UrlUtils } from 'src/utils';
import { formatFieldValue } from 'src/utils/CustomFieldsUtils';

import { PropertyFieldType } from './CustomFieldWrapper';

interface FieldValidation {
  invalidText: string;
}
interface FieldDefaultProps {
  icon: IconType;
  placeholder: string;
  validation?: FieldValidation;
}

export const PropertyFieldMappings = {
  phoneNumber: {
    icon: 'MobileNumber',
    placeholder: 'Add phone number',
    validation: {
      invalidText: 'Phone number is invalid',
    },
  },
  email: {
    icon: 'Email',
    placeholder: 'Add email',
    validation: {
      invalidText: 'Enter a valid email',
    },
  },
  url: {
    icon: 'Link',
    placeholder: 'Add a URL',
    validation: {
      invalidText: 'Enter a valid URL',
    },
  },
  text: {
    icon: 'Text',
    placeholder: 'Add text',
  },
  number: {
    icon: 'Number',
    placeholder: 'Add number',
    validation: {
      invalidText: 'Enter a valid number',
    },
  },
  address: {
    icon: 'Location',
    placeholder: 'Add location',
  },
  multiSelect: {
    icon: 'Tag',
    placeholder: 'Add tag',
  },
} satisfies Record<CustomFieldType, FieldDefaultProps>;

type InputTypes = {
  value: string;
  onChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void | ((val: string) => void);
  error?: boolean;
  helperText?: string | boolean;
  onBlur?: () => void;
  type: CustomFieldType;
  placeholder: string;
  onHeightChange?: (height: number) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
};

const ResizableInputField = ({
  type,
  value,
  onChange,
  error,
  helperText,
  onBlur,
  placeholder,
  onHeightChange,
  onKeyDown,
  autoFocus,
}: InputTypes) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      const newHeight = Math.max(32, textareaRef.current.scrollHeight);
      textareaRef.current.style.height = `${newHeight}px`;
      onHeightChange?.(newHeight);
    }
  }, [value, onHeightChange]);

  // Set cursor at end only on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [autoFocus]);

  return (
    <div className="w-full relative h-8">
      <div className="absolute top-[8.5px] z-10 h-4 w-4 ml-1 p-0.5 box-border flex items-center justify-center ">
        <Icon
          icon={PropertyFieldMappings[type].icon}
          className="h-3 w-3 text-light-gray"
        />
      </div>

      <textarea
        ref={textareaRef}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={clsx(
          'resize-none h-8 min-h-[32px] w-full py-[7px] pl-[23px] box-border text-sm placeholder:text-light-gray leading-4 border-black-heading rounded bg-gray-100 shadow-input',
          {
            'border-error-primary focus:border-error-primary': error,
          },
        )}
        value={value}
        onChange={onChange}
        style={{ overflow: 'hidden' }}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {error && helperText && (
        <ErrorMessage
          className="flex justify-end text-sm font-normal leading-none"
          message={typeof helperText === 'string' ? helperText : ''}
        />
      )}
    </div>
  );
};

type EditableInputProps = {
  type: CustomFieldType;
  value: string;
  validate: (val: string) => string | null;
  onUpdate: (val: string) => void;
  fieldValue: string;
  isEditing: boolean;
  onSetIsEditing: (isEditing: boolean) => void;
};

export const EditableInput = ({
  type,
  value = '',
  validate,
  fieldValue,
  onUpdate,
  isEditing,
  onSetIsEditing,
}: EditableInputProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [draftFieldValue, setDraftFieldValue] = useState(value);
  const [showError, setShowError] = useState(false);
  const [errMessage, setErrMessage] = useState('');
  const [containerHeight, setContainerHeight] = useState(32);
  const { ref: sidebarRef, rect } = useResizeObserver<HTMLDivElement>();

  useEffect(() => {
    setInputValue(value);
    if (!isEditing) {
      setDraftFieldValue(value);
    }
  }, [value, isEditing]);

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | string,
  ) => {
    const newValue = typeof event === 'string' ? event : event?.target?.value;
    const validatedInputValue = typeof newValue === 'string' ? newValue : '';
    if (validate) {
      const validationErrorMessage = validate(validatedInputValue);
      if (validationErrorMessage) {
        setErrMessage(validationErrorMessage);
      } else {
        setErrMessage('');
      }
    }
    setInputValue(validatedInputValue);
  };

  const handleUpdate = () => {
    if (!inputValue) {
      onUpdate(inputValue);
      return;
    }

    if (inputValue && inputValue.length > 0) {
      onUpdate(inputValue);
    }
  };

  const handleBlur = () => {
    let validationErrorMessage = '';
    if (validate) {
      validationErrorMessage = validate(inputValue) || '';
    }
    if (validationErrorMessage) {
      setErrMessage(validationErrorMessage);
      setShowError(true);
      onSetIsEditing(true);
      return;
    }
    setErrMessage('');
    setShowError(false);
    onSetIsEditing(false);
    handleUpdate();
  };

  const handleHeightChange = (height: number) => {
    setContainerHeight(height);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      handleBlur();
    }
  };

  useEffect(() => {
    if (!isEditing) {
      setContainerHeight(32);
    }
  }, [isEditing]);

  const handleEditField = () => {
    onSetIsEditing(true);
    setInputValue(draftFieldValue);
    setShowError(false);
  };

  return (
    <div
      ref={sidebarRef}
      className="w-full relative"
      style={{
        minHeight: `${containerHeight}px`,
        height: isEditing ? 'auto' : '32px',
      }}
    >
      {!isEditing ? (
        <button
          onClick={handleEditField}
          className="flex items-center min-h-8 w-full cursor-pointer appearance-none border-0 bg-transparent px-0"
          aria-label="Edit field"
          // The text max width is calculated dynamically based on the available space.
          // - `rect.width - 10`: Leaves a 10px padding/margin buffer so the element doesn’t touch the sidebar container’s edge.
          // - `60`: Ensures the element never shrinks below 60px, providing a minimum readable width.
          style={{
            maxWidth: Math.max(60, rect.width - 10),
          }}
        >
          {type === PropertyFieldType.URL && fieldValue ? (
            <TextLink
              href={UrlUtils.GetFullUrl(fieldValue)}
              target="_blank"
              className="text-sm text-ellipsis whitespace-nowrap overflow-hidden leading-[21px]"
              onClick={(e) => e.stopPropagation()}
            >
              {fieldValue
                ? formatFieldValue(
                    type,
                    typeof fieldValue === 'string' ? fieldValue : '',
                  )
                : PropertyFieldMappings[type].placeholder}
            </TextLink>
          ) : (
            <Body
              size="sm"
              className={clsx(
                'text-ellipsis whitespace-nowrap overflow-hidden leading-[21px]',
                {
                  'text-black-heading': fieldValue,
                  'text-light-gray': !fieldValue,
                },
              )}
            >
              {fieldValue
                ? formatFieldValue(
                    type,
                    typeof fieldValue === 'string' ? fieldValue : '',
                  )
                : PropertyFieldMappings[type].placeholder}
            </Body>
          )}
        </button>
      ) : (
        <>
          {type === PropertyFieldType.PHONE_NUMBER ? (
            <>
              <div className="absolute top-[8.5px] z-10 h-4 w-4 ml-1 p-0.5 box-border flex items-center justify-center">
                <Icon
                  icon={PropertyFieldMappings[type].icon}
                  className="h-3 w-3 text-light-gray"
                />
              </div>
              <PhoneNumberInput
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={PropertyFieldMappings[type].placeholder}
                autoFocus
                className="w-full [&>div>input]:pl-6! [&>div>input]:mt-[-.5px] [&>div>input]:placeholder:text-sm [&>div>input]:h-8! [&>div>input]:bg-gray-100"
                error={Boolean(errMessage) && showError}
                helperText={showError && errMessage}
              />
            </>
          ) : (
            <div className={clsx({ 'pb-5': showError && errMessage })}>
              <ResizableInputField
                type={type}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                error={Boolean(errMessage) && showError}
                helperText={showError && errMessage}
                onBlur={handleBlur}
                placeholder={PropertyFieldMappings[type].placeholder}
                onHeightChange={handleHeightChange}
                autoFocus
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
